// Subpage_112.jsx
import React, { useState } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import TabsBase from "../../components/reusable/Navigation/TabsBase";
import ContainerBase from "../../components/framepart/SubpageLayout/ContainerBase";
import ToolbarHeadSubpage from "../../components/framepart/SubpageLayout/ToolbarHeadSubpage";
import CardRisk from "../../components/reusable/Card/CardRisk";

import KpiGroup from "../../components/reusable/Forms/KpiGroup";
import RiskMatrix from "../../components/defined/Canevas/RiskMatrix";
import RiskMatrixLegendISO from "../../components/defined/Canevas/RiskMatrixLegendISO";
import DropdownMulti from "../../components/reusable/Forms/DropdownMulti";
import DropdownList from "../../components/reusable/Forms/DropdownList";
import Button from "../../components/reusable/Forms/Button";
import TextFilter from "../../components/reusable/Forms/TextFilter";
import Switch from "../../components/reusable/Forms/Switch";
import RiskReportPreview from "../../components/defined/Report/RiskReportPreview";
import PrintMenuBase from "../../components/defined/Report/PrintMenuBase";

import {
  RISKS_CATEGORY_OPTIONS,
  RISKS_STATUS_OPTIONS,
  RISKS_CRITICITY_OPTIONS,
} from "../../base/constants";

// ============================================================================
// Constantes / options
// ============================================================================
const statutOptions = RISKS_STATUS_OPTIONS;
const categorieOptions = RISKS_CATEGORY_OPTIONS;
const criticiteOptions = RISKS_CRITICITY_OPTIONS;

const responsableOptions = [
  { value: "dupont", label: "J. Dupont" },
  { value: "martin", label: "S. Martin" },
  { value: "diallo", label: "A. Diallo" },
];

const phaseOptions = [
  { value: "ETUDES", label: "Études" },
  { value: "TRAVAUX", label: "Travaux" },
  { value: "ACHAT", label: "Achats" },
];

const projetOptions = [
  { value: "p001", label: "Projet Eau Potable" },
  { value: "p002", label: "Projet Centrale Solaire" },
  { value: "p003", label: "Projet Réhabilitation" },
];

const IMPACT_LABELS = [
  "Insignifiant",
  "Mineur",
  "Modéré",
  "Majeur",
  "Catastrophique",
];

const PROBABILITY_LABELS = [
  "Improbable",
  "Peu probable",
  "Possible",
  "Probable",
  "Très probable",
];

// Mapping score → catégorie de criticité (alignée avec matrice 5x5)
const computeCriticityCategory = (impact, probabilite) => {
  if (!impact || !probabilite) return "";
  const score = impact * probabilite;
  if (score <= 4) return "LOW";
  if (score <= 13) return "MEDIUM";
  return "HIGH";
};

/* ============================================================
   FACTORISATION — Styles communs aux containers
============================================================ */
const baseLeftZoneStyle = {
  flex: 3,
  minWidth: 700,
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  flexShrink: 0,
};

const baseRightZoneStyle = {
  flex: 1,
  minWidth: 350,
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  boxSizing: "border-box",
  flexShrink: 0,
};



// ============================================================================
// Helper sections dynamiques (zone de droite)
// ============================================================================
const buildDetailSections = (selectedRisk) => {
  if (!selectedRisk) {
    return [
      {
        icon: "/assets/icones/container/info.svg",
        title: "Aucun risque sélectionné",
        content: (
          <div style={{ fontSize: 13, color: theme.colors.text.body }}>
            Sélectionnez un risque dans le registre ou dans la matrice
            pour afficher ici ses détails d’évaluation.
          </div>
        ),
      },
    ];
  }

  const impactScore = selectedRisk.impact || 1;
  const probScore = selectedRisk.probabilite || 1;
  const criticityScore = impactScore * probScore;

  const impactLabel =
    IMPACT_LABELS[impactScore - 1] || IMPACT_LABELS[0];
  const probLabel =
    PROBABILITY_LABELS[probScore - 1] || PROBABILITY_LABELS[0];

  let criticityLevel = "Faible";
  if (criticityScore <= 4) criticityLevel = "Faible";
  else if (criticityScore <= 13) criticityLevel = "Moyenne";
  else criticityLevel = "Élevée";

  const criticityColor =
    criticityLevel === "Élevée"
      ? "red"
      : criticityLevel === "Moyenne"
      ? "yellow"
      : "green";

  return [
    {
      icon: "/assets/icones/container/shield.svg",
      title: "Titre",
      content: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {selectedRisk.title || selectedRisk.label}
          </div>
          <div style={{ fontSize: 13, color: theme.colors.text.body }}>
            ID : {selectedRisk.id} · Statut : {selectedRisk.statut || "-"}
          </div>
        </div>
      ),
    },
    {
      icon: "/assets/icones/container/clipboard-list.svg",
      title: "Description du risque",
      content: (
        <div style={{ fontSize: 13, color: theme.colors.text.body }}>
          {selectedRisk.description || "Aucune description renseignée."}
        </div>
      ),
    },
    {
      icon: "/assets/icones/container/search-check.svg",
      title: "Évaluation (matrice)",
      content: (
        <div style={{ gap: 0 }}>
          <div style={{ gap: 0, marginBottom: 8 }}>
            <KpiGroup
              color="white"
              bordered
              rounded={false}
              orientation="horizontal"
              textSize="small"
              itemWidth="98%"
              itemHeight={50}
              gap={8}
              groups={[
                {
                  label: `Criticité (${criticityLevel})`,
                  value: criticityScore,
                  color: criticityColor,
                },
              ]}
            />
          </div>
          <div style={{ gap: 0 }}>
            <KpiGroup
              color="white"
              bordered
              rounded={false}
              orientation="horizontal"
              textSize="small"
              itemWidth="48%"
              itemHeight={50}
              gap={8}
              groups={[
                {
                  label: `Impact (${impactLabel})`,
                  value: impactScore,
                  color: "grey",
                },
                {
                  label: `Probabilité (${probLabel})`,
                  value: probScore,
                  color: "yellow",
                },
              ]}
            />
          </div>
        </div>
      ),
    },
    {
      icon: "/assets/icones/container/target.svg",
      title: "Mesures de mitigation",
      content: (
        <div style={{ fontSize: 13, color: theme.colors.text.body }}>
          Mesures de mitigation à détailler pour ce risque.
        </div>
      ),
    },
    {
      icon: "/assets/icones/container/crosshair.svg",
      title: "Risque résiduel",
      content: (
        <div style={{ fontSize: 13, color: theme.colors.text.body }}>
          Probabilité et impact résiduels à définir.
        </div>
      ),
    },
    {
      icon: "/assets/icones/container/clipboard-check.svg",
      title: "Plan d'actions",
      content: (
        <div style={{ fontSize: 13, color: theme.colors.text.body }}>
          Plan d'action à structurer (actions, responsables, échéances).
        </div>
      ),
    },
  ];
};

// ============================================================================
// Composant principal
// ============================================================================
const Subpage_112 = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const [editingRiskId, setEditingRiskId] = useState(null);
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Filtres
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedResponsables, setSelectedResponsables] = useState([]);

  const filterCategoryOptions = categorieOptions.map((c) => ({
    value: c.value,
    label: c.label,
  }));

  const filterStatusOptions = statutOptions.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const [risks, setRisks] = useState([
    {
      id: 1,
      label: "Risque #1",
      title: "Retard livraison turbines",
      description:
        "Retard possible de 3-6 mois causé par le fournisseur principal.",
      responsable: "dupont",
      phase: "achat",
      projet: "p001",
      statut: "Open",
      categorie: "Planning",

      impact: 2,
      probabilite: 3,
      criticite: computeCriticityCategory(2, 3),

      createAt: "",
      closeAt: "",
    },
    {
      id: 2,
      label: "Risque #2",
      title: "Surcharge hydraulique",
      description: "Risque de surcharge du réseau pendant les crues.",
      responsable: "martin",
      phase: "etudes",
      projet: "p002",
      statut: "Open",
      categorie: "Planning",

      impact: 4,
      probabilite: 4,
      criticite: computeCriticityCategory(4, 4),

      createAt: "",
      closeAt: "",
    },
    {
      id: 3,
      label: "Risque #3",
      title: "Inflation",
      description: "Hausse des prix sur les matériaux importés.",
      responsable: "diallo",
      phase: "travaux",
      projet: "p003",
      statut: "Open",
      categorie: "Planning",

      impact: 3,
      probabilite: 2,
      criticite: computeCriticityCategory(3, 2),

      createAt: "",
      closeAt: "",
    },
  ]);

  const addRisk = () => {
    const impact = 3;
    const probabilite = 3;
    const newRisk = {
      id: Date.now(),
      label: "Nouveau risque",
      title: "",
      description: "",
      responsable: "",
      phase: "",
      projet: "",
      statut: "",
      categorie: "",
      criticite: computeCriticityCategory(impact, probabilite),

      impact,
      probabilite,

      createAt: "",
      closeAt: "",
    };
    setRisks((prev) => [newRisk, ...prev]);
  };

  // Centralisation de la mise à jour + recalcul criticité
  const updateRisk = (id, field, value) => {
    setRisks((prev) =>
      prev.map((risk) => {
        if (risk.id !== id) return risk;

        const updated = { ...risk, [field]: value };

        const impactNum =
          typeof updated.impact === "number"
            ? updated.impact
            : parseInt(updated.impact, 10);
        const probNum =
          typeof updated.probabilite === "number"
            ? updated.probabilite
            : parseInt(updated.probabilite, 10);

        if (
          !Number.isNaN(impactNum) &&
          !Number.isNaN(probNum) &&
          impactNum > 0 &&
          probNum > 0
        ) {
          updated.impact = impactNum;
          updated.probabilite = probNum;
          updated.criticite = computeCriticityCategory(impactNum, probNum);
        }

        return updated;
      })
    );
  };

  const deleteRisk = (id) => {
    setRisks((prev) => prev.filter((risk) => risk.id !== id));
    setSelectedRiskId((prev) => (prev === id ? null : prev));
    setEditingRiskId((prev) => (prev === id ? null : prev));
  };

  const archiveRisk = (id) => {
    setRisks((prev) =>
      prev.map((risk) =>
        risk.id === id ? { ...risk, statut: "clos" } : risk
      )
    );
  };

  const handleSelect = (riskId) => {
    if (editingRiskId && editingRiskId !== riskId) return;
    if (editingRiskId === riskId) return;
    setSelectedRiskId((prev) => (prev === riskId ? null : riskId));
  };

  const handleToggleEdit = (riskId) => {
    setEditingRiskId((prev) => (prev === riskId ? null : riskId));
    setSelectedRiskId(riskId);
  };

  const guarded = (riskId, action) => {
    if (editingRiskId && editingRiskId !== riskId) return;
    action();
  };

  const selectedRisk = risks.find((r) => r.id === selectedRiskId) || null;

  const handleGenerateRiskDocument = (options) => {
    console.log("Génération rapport risques avec options :", options);
  };

  // ==========================================================================
  // ZONES
  // ==========================================================================

  const zoneA1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Risques",
        content: (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {risks.map((r) => (
              <CardRisk
                key={r.id}
                label={r.label}
                title={r.title}
                description={r.description}
                responsable={r.responsable}
                phase={r.phase}
                projet={r.projet}
                statut={r.statut}
                categorie={r.categorie}
                criticite={r.criticite}
                responsableOptions={responsableOptions}
                phaseOptions={phaseOptions}
                projetOptions={projetOptions}
                statutOptions={statutOptions}
                categorieOptions={categorieOptions}
                criticiteOptions={criticiteOptions}
                onChangeLabel={(v) => updateRisk(r.id, "label", v)}
                onChangeTitle={(v) => updateRisk(r.id, "title", v)}
                onChangeDescription={(v) =>
                  updateRisk(r.id, "description", v)
                }
                onChangeResponsable={(v) =>
                  updateRisk(r.id, "responsable", v)
                }
                onChangePhase={(v) => updateRisk(r.id, "phase", v)}
                onChangeProjet={(v) => updateRisk(r.id, "projet", v)}
                onChangeStatut={(v) => updateRisk(r.id, "statut", v)}
                onChangeCategorie={(v) => updateRisk(r.id, "categorie", v)}
                onChangeCriticite={(v) => updateRisk(r.id, "criticite", v)}
                createAt={r.createAt}
                closeAt={r.closeAt}
                onChangeCreateAt={(v) => updateRisk(r.id, "createAt", v)}
                onChangeCloseAt={(v) => updateRisk(r.id, "closeAt", v)}
                impact={r.impact}
                probabilite={r.probabilite}
                onChangeImpact={(v) => updateRisk(r.id, "impact", v)}
                onChangeProbabilite={(v) =>
                  updateRisk(r.id, "probabilite", v)
                }
                editMode={editingRiskId === r.id}
                onToggleEdit={() => handleToggleEdit(r.id)}
                onArchive={() => guarded(r.id, () => archiveRisk(r.id))}
                onDelete={() => guarded(r.id, () => deleteRisk(r.id))}
                selected={selectedRiskId === r.id}
                onSelect={() => handleSelect(r.id)}
                actionsDisabled={
                  editingRiskId !== null && editingRiskId !== r.id
                }
              />
            ))}
          </div>
        ),
      },
    ],
  };

  // A2 dynamique (même logique que B2)
  const zoneA2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: buildDetailSections(selectedRisk),
  };

  const zoneB1 = {
    style: {
      ...baseLeftZoneStyle,
      paddingBottom: 10,
    },
    gap: 16,
    sectionGap: 0,
    sections: [
      {
        title: "Impact / Probabilité",
        content: (
          <div style={{ padding: 0 }}>
            <RiskMatrixLegendISO />
            <RiskMatrix
              rows={5}
              cols={5}
              risks={risks}
              onRiskSelect={(id) => setSelectedRiskId(id)}
            />
          </div>
        ),
      },
    ],
  };

  // B2 dynamique (inchangé, mais factorisé via helper)
  const zoneB2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: buildDetailSections(selectedRisk),
  };

  const zoneC1 = {
    style: {
      ...baseLeftZoneStyle,
      paddingBottom: 10,
    },
    gap: 16,
    sectionGap: 0,
    sections: [
      {
        title: "Aperçu du risque",
        content: selectedRisk ? (
          <RiskReportPreview risk={selectedRisk} />
        ) : (
          <div style={{ padding: 8, fontSize: 14 }}>
            Aucun risque sélectionné.
          </div>
        ),
      },
    ],
  };

  const zoneC2 = {
    style: {
      ...baseRightZoneStyle,
      paddingBottom: 10,
    },
    gap: 16,
    sectionGap: 0,
    sections: [
      {
        title: "Paramètres d’impression",
        content: (
          <PrintMenuBase
            items={risks}
            templates={[
              { value: "risks_default", label: "Rapport risques – Standard" },
              { value: "risks_detail", label: "Rapport risques – Détaillé" },
            ]}
            defaultTemplate="risks_default"
            onGenerate={handleGenerateRiskDocument}
          />
        ),
      },
    ],
  };

    /* ============================================================
     ZONE D1
  ============================================================ */
  const zoneD1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Zone D1 — Vue générale",
        content: <div>Aucun élément sélectionné.</div>,
      },
    ],
  };

  /* ============================================================
     ZONE D2
  ============================================================ */
  const zoneD2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Zone D2 — Paramètres",
        content: <div>Configuration non définie…</div>,
      },
    ],
  };
  /* ============================================================
     ZONE E1
  ============================================================ */
  const zoneE1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Zone E1 — Vue générale",
        content: <div>Aucun élément sélectionné.</div>,
      },
    ],
  };

  /* ============================================================
     ZONE E2
  ============================================================ */
  const zoneE2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Zone E2 — Paramètres",
        content: <div>Configuration non définie…</div>,
      },
    ],
  };
 

  // ========================================================================
  // TABS
  // ========================================================================
  const TabA = (
    <div style={tabStyle}>
      <ContainerBase {...zoneA1} />
      <ContainerBase {...zoneA2} />
    </div>
  );

  const TabB = (
    <div style={tabStyle}>
      <ContainerBase {...zoneB1} />
      <ContainerBase {...zoneB2} />
    </div>
  );

  const TabC = (
    <div style={tabStyle}>
      <ContainerBase {...zoneC1} />
      <ContainerBase {...zoneC2} />
    </div>
  );

  const TabD = (
    <div style={tabStyle}>
      <ContainerBase {...zoneD1} />
      <ContainerBase {...zoneD2} />
    </div>
  );

  const TabE = (
    <div style={tabStyle}>
      <ContainerBase {...zoneE1} />
      <ContainerBase {...zoneE2} />
    </div>
  );
 

  const tabs = [
    { label: "Registre", content: TabA },
    { label: "Matrice", content: TabB },
    { label: "Reports", content: TabC },
    // { label: "Mitigation Planning", content: TabD },
    // { label: "Reports & Dashboards", content: TabE },
   
  ];

  // ========================================================================
  // RENDER
  // ========================================================================
  return (
    <div
      style={{
        padding: 0,
        margin: 0,
        background: "transparent",
        width: "100%",
        height: "100%",
        overflowX: "hidden",
        overflowY: "visible",
        boxSizing: "border-box",
      }}
    >
      <ToolbarHeadSubpage
  disabled={editingRiskId !== null}
  height={56}

  /* --- ZONE GAUCHE --- */
  left={[
    <Button
      key="btn-add-risk"
      variant="blue"
      onClick={addRisk}
    >
      Nouveau risque
    </Button>,
  ]}

  /* --- ZONE CENTRALE --- */
  center={[
    <DropdownMulti
      key="dd-cat"
      label="Catégorie"
      options={filterCategoryOptions}
      selectedValues={selectedCategories}
      onChange={setSelectedCategories}
      width={300}
      height={32}
    />,
    <TextFilter
      key="search"
      label="Recherche"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Tapez un texte..."
    />,
  ]}

  /* --- ZONE DROITE --- */
  right={[
    <Switch
      key="switch"
      label="Actifs"
      enabled={switchOn}
      onChange={setSwitchOn}
      showText={false}
      size="small"
      switchStyle={{ transform: "translateY(2px)" }}
    />,
  ]}

  /* --- STYLE GÉNÉRAL --- */
  style={{
    marginTop: 0,
    padding: "0 24px",
    width: "100%",
    height: "56px",
    minHeight: "56px",
    background: "transparent",
    overflow: "visible",
    position: "relative",
    zIndex: 100,
  }}
/>


      <TabsBase
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsDisabled={editingRiskId !== null}
      />
    </div>
  );
};

/* ============================================================
   STYLE COMMUN TABS
============================================================ */
const tabStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 10,
  padding: 10,
  overflowY: "clip",
  overflowX: "hidden",
  boxSizing: "border-box",
  flexShrink: 0,
  minWidth: "fit-content",
  maxHeight: "calc(100vh - 250px)", // exception à vérifier par rapport aux autres subpages
};

export default Subpage_112;
